import { CODE_TYPES, EXCEL_TYPES, POWERPOINT_TYPES, WORD_TYPES, ZIP_TYPES } from '../constants/contentTypeConstants';
import { ICON } from '../constants/IconConstants';
import { MESSAGE_TYPE } from '../constants/MessageTypes';
import { calculateVideoUrl } from './videoHelper';

export const calculateMessageMediaURL = (mediaName: string) => {
  const baseUrl = import.meta.env.VITE_MEDIA_BASE_URL;
  return `${baseUrl}/${mediaName}`;
};

// TODO react-query
interface MessageType {
  messageType: string;
  mediaRelativePath?: string;
  filename?: string;
  contentType?: string;
  videoPlaybackId?: string;
  videoDownloadReady?: boolean;
  videoDownloadResolution?: string;
  videoTitle?: string;
}

const normalizeVideoFilename = (filename: string) => `${filename.replace(/\.[^/.]+$/, '')}.mp4`;

export const filesFromMessages = (messages: MessageType[]) =>
  messages
    .map((message) => {
      if ([MESSAGE_TYPE.MEDIA, MESSAGE_TYPE.MEDIA_FROM_MAIL].includes(message.messageType)) {
        if (!message.mediaRelativePath || !message.filename || !message.contentType) {
          return undefined;
        }

        return {
          url: calculateMessageMediaURL(message.mediaRelativePath),
          name: message.filename,
          type: message.contentType,
        };
      }

      if (message.messageType === MESSAGE_TYPE.VIDEO) {
        if (!message.videoDownloadReady || !message.videoPlaybackId || !message.videoTitle) {
          return undefined;
        }

        return {
          url: calculateVideoUrl(message.videoPlaybackId, message.videoDownloadResolution),
          name: normalizeVideoFilename(message.videoTitle),
          type: 'video/mp4',
        };
      }

      return undefined;
    })
    .filter((file): file is NonNullable<typeof file> => !!file);

// TODO react-query
interface MessageFileType {
  url: string;
  name: string;
  type: string;
}

export const downloadMessageFiles = (messageFiles: MessageFileType[]) =>
  Promise.all(
    messageFiles.map(async (messageFile) => {
      const response = await fetch(messageFile.url);
      const blob = await response.blob();
      const file = new File([blob], messageFile.name, { type: messageFile.type });

      return file;
    }),
  );

export const fileListKey = (file: File & { id?: string }): string =>
  file.id || `${file.name}:${file.type}:${file.size}:${file.lastModified}`;

export const mimeTypeIsImage = (attachmentType: string | undefined) =>
  !!attachmentType && /^image\//.test(attachmentType);

export const calculateMediaIconFromType = (contentType: string | undefined) => {
  if (!contentType) {
    return ICON.FILE;
  }
  if (contentType.startsWith('image')) {
    return ICON.FILE_IMAGE;
  }
  if (contentType.startsWith('video')) {
    return ICON.FILE_VIDEO;
  }
  if (contentType.startsWith('audio')) {
    return ICON.FILE_AUDIO;
  }
  if (contentType === 'application/pdf') {
    return ICON.FILE_PDF;
  }
  if (contentType === 'text/plain') {
    return ICON.FILE_TEXT;
  }
  if (ZIP_TYPES.includes(contentType)) {
    return ICON.FILE_ARCHIVE;
  }
  if (WORD_TYPES.includes(contentType)) {
    return ICON.FILE_WORD;
  }
  if (EXCEL_TYPES.includes(contentType)) {
    return ICON.FILE_EXCEL;
  }
  if (POWERPOINT_TYPES.includes(contentType)) {
    return ICON.FILE_POWERPOINT;
  }
  if (CODE_TYPES.includes(contentType)) {
    return ICON.FILE_CODE;
  }
  return ICON.FILE;
};
